/**
 * MCS 032:2025 §4.3 to §4.5: the benefits of a battery beyond the
 * self-consumption tables. The installer allocates a share of usable
 * capacity to self-consumption (§4.4.1); the remainder is assumed to cycle
 * twice a day (§4.3.1) and is reported as energy discharged per year.
 * Any further self-consumption from an EV, a heat pump or a diverter may be
 * estimated by a suitable method (§4.2.4), capped so that total
 * self-consumption never exceeds 95% of generation or of consumption.
 */

/** §4.3.1: two full cycles a day over a year. */
export const EESS_DISCHARGE_CYCLES_PER_YEAR = 730;
/** §4.2.4: the ceiling on total self-consumption, as a share of the smaller of generation and consumption. */
export const EESS_SELF_CONSUMPTION_CAP = 0.95;

const round2 = (value: number): number => Math.round(value * 100) / 100;

/** The usable capacity set aside for self-consumption, kWh (§4.4.1). */
export const eessAllocatedCapacityKwh = (
  usableKwh: number,
  allocationPercent: number
): number =>
  round2((usableKwh * Math.min(100, Math.max(0, allocationPercent))) / 100);

export type EessBenefitsInput = {
  usableKwh: number;
  /** Share of usable capacity allocated to self-consumption, 0 to 100. */
  allocationPercent: number;
  annualGenerationKwh: number;
  annualConsumptionKwh: number;
  /** The section D figure, worked out at the allocated capacity. */
  selfConsumedWithEessKwh: number;
  /**
   * Extra self-consumption from EV charging, heat pumps or diverters before
   * the cap, from whatever method the installer used. Null when not
   * modelled, which leaves the row off the document.
   */
  additionalLoadsKwh?: number | null;
};

export type EessBenefits = {
  allocatedKwh: number;
  notAllocatedKwh: number;
  /** Capacity not allocated to self-consumption, converted to kWh a year. */
  dischargedKwhPerYear: number;
  /** Null when no additional loads were modelled. */
  additionalSelfConsumptionKwh: number | null;
};

export const eessAdditionalBenefits = (
  input: EessBenefitsInput
): EessBenefits => {
  const allocatedKwh = eessAllocatedCapacityKwh(
    input.usableKwh,
    input.allocationPercent
  );
  const notAllocatedKwh = round2(Math.max(0, input.usableKwh - allocatedKwh));
  const dischargedKwhPerYear = Math.round(
    notAllocatedKwh * EESS_DISCHARGE_CYCLES_PER_YEAR
  );
  const additionalSelfConsumptionKwh =
    input.additionalLoadsKwh == null
      ? null
      : Math.round(
          Math.max(
            0,
            Math.min(
              input.additionalLoadsKwh,
              input.annualGenerationKwh * EESS_SELF_CONSUMPTION_CAP -
                input.selfConsumedWithEessKwh,
              input.annualConsumptionKwh * EESS_SELF_CONSUMPTION_CAP -
                input.selfConsumedWithEessKwh
            )
          )
        );
  return {
    allocatedKwh,
    notAllocatedKwh,
    dischargedKwhPerYear,
    additionalSelfConsumptionKwh,
  };
};
